import {useEffect, useState} from "react"
import {
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    Typography,
    Grid,
    Box,
    Divider,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableRow,
    Paper,
    Button,
    useMediaQuery,
    useTheme,
} from "@mui/material"
import CloseIcon from "@mui/icons-material/Close"
import {useTranslation} from "react-i18next"
import AddShoppingCartIcon from "@mui/icons-material/AddShoppingCart"
import QuantitySelector from "../components/quantity-selector"
import dayjs from "dayjs"
import {useBasket} from "../providers/basket-context"
import axiosInstance from "../config/axios-config"
import ProductImagePlaceholder from "./product-image-placeholder"
import AppliedDiscount from "../components/common/applied-discount";
import {PhotoCamera as PhotoCameraIcon} from "@mui/icons-material";

const ProductDetailsDialog = ({open, onClose, product, tenantType, readOnly = false}) => {
    const {t} = useTranslation()
    const [quantity, setQuantity] = useState(1)
    const theme = useTheme()
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"))
    const {addToBasket} = useBasket()
    const [photo, setPhoto] = useState(null)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState(null)

    // Load product photo if available
    useEffect(() => {
        let isMounted = true
        const fetchProductPhoto = async () => {
            if (!product?.imageUrl) {
                setPhoto(null)
                return
            }
            setLoading(true)
            setError(null)
            try {
                const response = await axiosInstance.get(`/api/v1/products/photo/${product.imageUrl}`, {
                    responseType: "blob",
                })

                if (isMounted) {
                    setPhoto(URL.createObjectURL(response.data))
                }
            } catch (err) {
                console.error("Error fetching image:", err)
                if (isMounted) {
                    setError(err)
                    setPhoto(null)
                }
            } finally {
                if (isMounted) {
                    setLoading(false)
                }
            }
        }

        fetchProductPhoto().then(r => console.log("Product photo fetched successfully"))

        // Cleanup function
        return () => {
            isMounted = false
            // Revoke object URL to prevent memory leaks
            if (photo) {
                URL.revokeObjectURL(photo)
            }
        }
    }, [product?.imageUrl]) // Only depend on imageUrl

    // Cleanup photo URL when component unmounts
    useEffect(() => {
        return () => {
            if (photo) {
                URL.revokeObjectURL(photo)
            }
        }
    }, [photo])

    if (!product) {
        return null
    }

    const handleImageUpload = async (event) => {
        const selectedFile = event.target.files[0];
        const formData = new FormData();
        formData.append('file', selectedFile);
        if (selectedFile) {
            try {
                await axiosInstance.post(`/api/v1/products/photo/${product.productId}`, formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                });
                const productResponse = await axiosInstance.get(`/api/v1/products/${product.productId}`);
                const filename = productResponse.data.imageUrl;
                const response = await axiosInstance.get(`/api/v1/products/photo/${filename}`, {
                    responseType: 'blob',
                });
                // Create URL from blob (to display image in UI)
                setPhoto(URL.createObjectURL(response.data));
            } catch (error) {
                console.error("Upload failed:", error);
            }
        }
    };

    const handleQuantityChange = (newValue) => {
        setQuantity(newValue)
    }

    const handleAddToBasket = async () => {
        console.log(`Adding ${quantity} of ${product.productName} to basket`)
        try {
            await addToBasket({
                productTenantId: product.productTenantId,
                count: quantity,
            })
            onClose()
        } catch (error) {
            console.error("Error adding to basket:", error)
        }
    }

    const renderProductImage = () => {
        if (loading) {
            return (
                <Box
                    sx={{
                        width: "100%",
                        height: "250px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: "#f5f5f5",
                        borderRadius: 1,
                    }}
                >
                    <Typography variant="body2" color="text.secondary">
                        {t("Loading")}
                    </Typography>
                </Box>
            )
        }

        if (photo && !error) {
            return (
                <Box
                    component="img"
                    src={photo}
                    alt={product.productName}
                    sx={{
                        maxWidth: "100%",
                        maxHeight: "250px",
                        objectFit: "contain",
                    }}
                />
            )
        }

        // Show placeholder when no photo or error
        return <ProductImagePlaceholder product={product} variant="category" size={200}/>
    }

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: "8px",
                    overflow: "hidden",
                },
            }}
        >
            <DialogTitle
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    bgcolor: "#f5f5f5",
                    borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
                }}
            >
                <Typography variant="h6" component="div" sx={{fontWeight: 500}}>
                    {t("ProductDetails")}
                </Typography>
                <IconButton edge="end" color="inherit" onClick={onClose} aria-label="close">
                    <CloseIcon/>
                </IconButton>
            </DialogTitle>
            <DialogContent sx={{p: 0}}>
                <Grid container>
                    {/* Product Image */}
                    <Grid
                        item
                        xs={12}
                        md={4}
                        sx={{
                            bgcolor: "#fff",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            p: 3,
                            borderRight: { md: "1px solid rgba(0, 0, 0, 0.12)" },
                        }}
                    >
                        <Box sx={{ position: "relative" }}>
                            {renderProductImage()}

                            {/* Upload icon */}
                            {tenantType === "SYSTEM" && <IconButton
                                component="label"
                                sx={{
                                    position: "absolute",
                                    bottom: 8,
                                    right: 8,
                                    bgcolor: "rgba(0,0,0,0.6)",
                                    color: "#fff",
                                    "&:hover": {
                                        bgcolor: "rgba(0,0,0,0.8)",
                                    },
                                }}
                            >
                                <PhotoCameraIcon />
                                <input hidden accept="image/*" type="file" onChange={handleImageUpload}/>
                            </IconButton> }
                        </Box>
                    </Grid>

                    {/* Product Details */}
                    <Grid item xs={12} md={8} sx={{p: 3}}>
                        <Typography variant="h5" gutterBottom sx={{fontWeight: 500}}>
                            {product.productName}
                        </Typography>

                        <Box sx={{display: "flex", gap: 1, mb: 2}}>
                            <Chip label={t(product.productCategory)} size="small" color="primary" variant="outlined"/>
                            <Chip label={`${t("Code")}: ${product.productCode}`} size="small" variant="outlined"/>
                        </Box>

                        <Divider sx={{my: 2}}/>

                        <TableContainer component={Paper} variant="outlined" sx={{mb: 3}}>
                            <Table size="small">
                                <TableBody>
                                    {tenantType === "PHARMACY" && <TableRow>
                                        <TableCell component="th" sx={{fontWeight: 500, width: "40%"}}>
                                            {t("Distributor")}
                                        </TableCell>
                                        <TableCell>{product.tenantName}</TableCell>
                                    </TableRow>
                                    }
                                    <TableRow>
                                        <TableCell component="th" sx={{fontWeight: 500}}>
                                            {t("Manufacturer")}
                                        </TableCell>
                                        <TableCell>{product.productManufacturer}</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell component="th" sx={{fontWeight: 500}}>
                                            {t("Country")}
                                        </TableCell>
                                        <TableCell>{product.productCountry}</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell component="th" sx={{fontWeight: 500}}>
                                            {t("Package")}
                                        </TableCell>
                                        <TableCell>{tenantType === "IMPORTER" ? t(product.packageType) : t(product.productPackage)}</TableCell>
                                    </TableRow>
                                    {tenantType === "PHARMACY" && <TableRow>
                                        <TableCell component="th" sx={{fontWeight: 500}}>
                                            {t("ExpiryDate")}
                                        </TableCell>
                                        <TableCell>{product.endDate === null ? "**/****" : dayjs(product.endDate.toString()).format("YYYY/MM")}</TableCell>
                                    </TableRow>
                                    }
                                    {tenantType === "PHARMACY" && <TableRow>
                                        <TableCell component="th" sx={{fontWeight: 500}}>
                                            {t("AvailableQuantity")}
                                        </TableCell>
                                        <TableCell>{product.quantity}</TableCell>
                                    </TableRow>
                                    }
                                </TableBody>
                            </Table>
                        </TableContainer>

                        {/* Price and Add to Cart Section - Responsive Layout */}
                        {tenantType === "PHARMACY" && <Box
                            sx={{
                                display: "flex",
                                flexDirection: isMobile ? "column" : "row",
                                justifyContent: "space-between",
                                alignItems: isMobile ? "flex-start" : "center",
                                bgcolor: "#f9f9f9",
                                p: 2,
                                borderRadius: "4px",
                                mb: 3,
                                gap: 2,
                            }}
                        >
                            <Box sx={{minWidth: isMobile ? "100%" : "auto"}}>
                                <Typography variant="body2" color="text.secondary">
                                    {t("Price")}
                                </Typography>
                                <AppliedDiscount product={product}/>
                            </Box>
                            {!readOnly && <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 2,
                                    width: isMobile ? "100%" : "auto",
                                    justifyContent: isMobile ? "space-between" : "flex-end",
                                    mt: isMobile ? 1 : 0,
                                }}
                            >
                                <QuantitySelector
                                    initialValue={product.cartQuantity || 0}
                                    min={1}
                                    max={product.quantity}
                                    onChange={handleQuantityChange}
                                    onBlur={() => {
                                    }}
                                    onAddToBasket={() => {
                                    }}
                                    index={0}
                                    row={product}
                                />
                                <Button
                                    variant="contained"
                                    color="primary"
                                    startIcon={<AddShoppingCartIcon/>}
                                    onClick={handleAddToBasket}
                                    sx={{whiteSpace: "nowrap"}}
                                >
                                    {t("AddToCart")}
                                </Button>
                            </Box>}
                        </Box>
                        }

                        {/* Additional product description could go here */}
                        {product.description && (
                            <Box>
                                <Typography variant="subtitle1" sx={{fontWeight: 500, mb: 1}}>
                                    {t("Description")}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {product.description}
                                </Typography>
                            </Box>
                        )}
                    </Grid>
                </Grid>
            </DialogContent>
        </Dialog>
    )
}

export default ProductDetailsDialog
